import toast from '@components/Common/Toast.js';
import Modal from '@components/Modals/Modal.js';
import { UIDiv, UIImage, UIText } from '@ui';
import { SelectionManager } from '@utils/SelectionManager.js';

export default class TaskDownloadModal extends Modal {
  constructor(cadFile) {
    super(i18next.t('modal.taskDownload.title'));
    this.cadFile = cadFile;
    this.selectedReconstructions = null;
    this.isLoading = false;
    this.resolve = null;
  }

  createModal() {
    const modal = super.createModal();
    const formContainer = new UIDiv().addClass('space-y-4 w-full');
    this.cancelButton.addClass('hidden');

    const grid = new UIDiv().addClass('grid grid-cols-4 gap-4 p-4');
    grid.setId('reconstruction-grid');

    if (
      this.cadFile.reconstructions &&
      this.cadFile.reconstructions.length > 0
    ) {
      this.selectionManager = new SelectionManager({
        container: this.dialog.dom,
        itemSelector: '.reconstruction-item',
        selectedClass: 'selected',
        addedClass:
          'border-2 !border-blue-500 relative after:content-[""] after:absolute after:inset-0 after:bg-blue-500/10 after:pointer-events-none',
        getItemId: el => el.dataset.reconstructionId,
        onSelectionChange: (selectedElements, selectedItems) => {
          this.selectedReconstructions = selectedItems;
          this.actionButton.setDisabled(selectedItems.size === 0);
          this.actionButton.setInnerText(
            i18next.t('modal.taskDownload.download') +
              ` (${selectedItems.size})`,
          );
        },
        enableShiftSelect: true,
        enableCtrlSelect: true,
        enableDragSelect: true,
        ignoreSelector: 'button, input, select, a, [role="button"]',
      });

      this.cadFile.reconstructions.forEach(reconstruction => {
        const item = this.createReconstructionItem(reconstruction);
        grid.add(item);
      });
    } else {
      const noModels = new UIDiv()
        .addClass('no-models text-center text-gray-500 py-4')
        .setTextContent(i18next.t('modal.taskDownload.no_models'));
      formContainer.add(noModels);
    }

    formContainer.add(grid);
    this.contentContainer.add(formContainer);

    this.actionButton
      .setInnerText(i18next.t('modal.taskDownload.download'))
      .addClass('px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700')
      .setDisabled(true);

    return modal;
  }

  createReconstructionItem(reconstruction) {
    const item = new UIDiv().addClass(
      'reconstruction-item relative border border-gray-300 rounded-md overflow-hidden cursor-pointer transition-all duration-200',
    );

    // Set unique ID for selection manager
    item.dom.dataset.reconstructionId =
      reconstruction.fileOriginalName || reconstruction.modelName;

    if (this.selectionManager) {
      this.selectionManager.registerItem(item.dom, reconstruction);
    }

    const thumbnailContainer = new UIDiv().addClass(
      'overflow-hidden bg-gray-100 aspect-square',
    );

    const thumbnail = new UIImage(
      reconstruction.thumbnail || '/static/images/logos/bee_blue.svg',
    );
    thumbnail.addClass('w-full h-full object-cover');
    thumbnailContainer.add(thumbnail);

    const info = new UIDiv().addClass(
      'flex flex-col items-center justify-between p-2',
    );
    const name = new UIText(
      reconstruction.fileOriginalName || reconstruction.modelName,
    ).addClass('model-name font-medium mb-1 truncate w-full text-center');
    const size = new UIText(
      this.formatFileSize(reconstruction.fileSize),
    ).addClass('model-size text-sm text-gray-500');

    info.add(name, size);
    item.add(thumbnailContainer, info);

    item.dom.addEventListener('mouseenter', () => {
      if (!item.dom.classList.contains('selected')) {
        item.addClass('hover:border-blue-300');
      }
    });

    item.dom.addEventListener('mouseleave', () => {
      if (!item.dom.classList.contains('selected')) {
        item.removeClass('hover:border-blue-300');
      }
    });

    return item;
  }

  formatFileSize(bytes) {
    if (!bytes) return i18next.t('modal.taskDownload.unknown_size');
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  async onAction() {
    if (this.isLoading) return;
    this.isLoading = true;
    this.actionButton.setInnerText(i18next.t('modal.taskDownload.downloading'));
    this.actionButton.setDisabled(true);

    try {
      for (const reconstruction of this.selectedReconstructions) {
        const response = await fetch(reconstruction.filePath);
        const blob = await response.blob();
        const fileName = `${reconstruction.fileOriginalName || reconstruction.modelName}.${reconstruction.filePath.split('.').pop()}`;
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(link.href);
      }
      this.close();
      toast.success(i18next.t('pages.projects.toast.download_task'));
    } catch (error) {
      console.error('Failed to download files:', error);
      toast.error(i18next.t('pages.projects.toast.download_task_error'));
      const errorMsg = new UIDiv()
        .addClass('text-red-500 mt-2')
        .setInnerText(i18next.t('modal.taskDownload.download_error'));
      this.contentContainer.add(errorMsg);
    } finally {
      this.isLoading = false;
      this.actionButton.setInnerText(i18next.t('modal.taskDownload.download'));
      this.actionButton.setDisabled(this.selectedReconstructions?.size === 0);
    }
  }

  show(elemId) {
    return new Promise(resolve => {
      this.resolve = resolve;
      super.show(elemId);
    });
  }

  close() {
    if (this.selectionManager) {
      this.selectionManager.destroy();
    }
    super.close();
  }
}
